import Vue from 'vue'
import { DashboardApi } from '@/api/dashboardApi'
import { DEFAULT_LAYOUT_ID } from '@/enum/generic'
import get from 'lodash/get'
import $axios from '@/api/apiConnection'
import { dashboardModel } from '@/models/instances'

function removeWidgetsFromDashboardObject (dashboard) {
    return {
        ...dashboard,
        WidgetGroupList: dashboard.WidgetGroupList.map(widgetGroup => {
            return {
                ...widgetGroup,
                WidgetList: []
            }
        })
    }
}

const types = {
    SET_ALL_DASHBOARDS: 'SET_ALL_DASHBOARDS',
    SET_EDIT_MODE: 'SET_EDIT_MODE',
    SET_ACTIVE_DASHBOARD: 'SET_ACTIVE_DASHBOARD',
    SET_ACTIVE_WIDGET_GROUP: 'SET_ACTIVE_WIDGET_GROUP',
    ADD_DASHBOARD: 'ADD_DASHBOARD',
    UPDATE_DASHBOARD: 'UPDATE_DASHBOARD',
    DELETE_DASHBOARD: 'DELETE_DASHBOARD',
    RESET_ACTIVE_DASHBOARD: 'RESET_ACTIVE_DASHBOARD',
    SET_LOADING: 'SET_LOADING',
    SET_CONTENT_LOADING: 'SET_CONTENT_LOADING',
    UPDATE_CURRENT_DASHBOARD: 'UPDATE_CURRENT_DASHBOARD',
    UPDATE_GROUP_LIST: 'UPDATE_GROUP_LIST',
    UPDATE_DASHBOARD_DATA: 'UPDATE_DASHBOARD_DATA',
    SET_ACTIVE_WIDGET_GROUP_ID: 'SET_ACTIVE_WIDGET_GROUP_ID',
}
const state = {
    allDashboards: [],
    activeDashboard: null,
    activeWidgetGroup: null,
    editMode: false,
    loadingData: false,
    contentLoading: false,
    activeWidgetGroupID: null,
}

const mutations = {
    [types.SET_ACTIVE_WIDGET_GROUP_ID]: (state, value) => {
        state.activeWidgetGroupID = String(value)
    },
    [types.SET_ALL_DASHBOARDS]: (state, value) => {
        state.allDashboards = value
    },
    [types.ADD_DASHBOARD]: (state, value) => {
        state.allDashboards.push(value)
    },
    [types.UPDATE_DASHBOARD]: (state, dashboard) => {
        const index = state.allDashboards.findIndex((el) => {
            if (!el || !dashboard) {
                return false
            }

            return el.DashboardID === dashboard.DashboardID
        })

        if (index === -1) {
            return
        }

        Vue.set(state.allDashboards, index, {
            ...dashboard,
            WidgetGroupList: []
        })

        state.activeDashboard = dashboard
    },
    [types.UPDATE_DASHBOARD_DATA]: (state, dashboard, dashboardData) => {
        const index = state.allDashboards.findIndex((el) => {
            if (!el || !dashboard) {
                return false
            }

            return el.DashboardID === dashboard.DashboardID
        })

        if (index !== -1) {
            return
        }

        Vue.set(
            state.allDashboards,
            index,
            {
                ...dashboard,
                ...dashboardData
            }
        )
    },
    [types.SET_ACTIVE_DASHBOARD]: (state, dashboard) => {
        state.activeDashboard = dashboard


        if (dashboard && !dashboard.DashboardLayoutID) {
            dashboard.DashboardLayoutID = DEFAULT_LAYOUT_ID
        }
    },
    [types.SET_ACTIVE_WIDGET_GROUP]: (state, group) => {
        state.activeWidgetGroup = group
    },
    [types.SET_EDIT_MODE]: (state, value) => {
        state.editMode = value
    },
    [types.DELETE_DASHBOARD]: (state, dashboard) => {
        let index = state.allDashboards.findIndex((d) => {
            if (!d || !dashboard) {
                return false
            }

            return d.DashboardID === dashboard.DashboardID
        })
        if (index !== -1) {
            state.allDashboards.splice(index, 1)
        }
    },
    [types.RESET_ACTIVE_DASHBOARD]: (state) => {
        state.activeDashboard = null
    },
    [types.SET_LOADING]: (state, loading) => {
        state.loadingData = loading
    },
    [types.SET_CONTENT_LOADING]: (state, loading) => {
        state.contentLoading = loading
    },
    [types.UPDATE_CURRENT_DASHBOARD]: (state, dashboard) => {
        state.activeDashboard = dashboard
    },
    [types.UPDATE_GROUP_LIST]: (state, value) => {
        state.activeDashboard.WidgetGroupList = value
    },
}

const actions = {
    setActiveWidgetGroupID({ commit }, value) {
        commit(types.SET_ACTIVE_WIDGET_GROUP_ID, value)
    },
    async getDashboards({ commit, state }, loadCached) {
        const load = async () => {
            commit(types.SET_LOADING, true)
            let dashboards = await DashboardApi.getAll()
            commit(types.SET_LOADING, false)
            commit(types.SET_ALL_DASHBOARDS, dashboards)
        }

        if (loadCached && state.allDashboards && state.allDashboards.length !== 0) {
            load()
        } else {
            await load()
        }
    },
    clearActiveDashboard ({ commit }) {
        commit(types.SET_ACTIVE_DASHBOARD, '')
    },
    async selectDashboard({ commit, state, getters }, data = {}) {
        let { dashboard, loadCached } = data

        const previousDashboard = state.activeDashboard

        if (!dashboard || dashboard.DashboardID === null || dashboard.DashboardID === undefined) {
            if (previousDashboard && previousDashboard.DashboardID) {
                dashboard = previousDashboard
            } else {
                dashboard = get(state, 'allDashboards.[0]', false)
            }
        }

        if (!dashboard) {
            return commit(types.SET_ACTIVE_DASHBOARD, '')
        }

        const load = async () => {
            if (!dashboard['DashboardLayoutID']) {
                dashboard['DashboardLayoutID'] = DEFAULT_LAYOUT_ID
                await DashboardApi.update(dashboard)
            }

            dashboard = await DashboardApi.find(dashboard.DashboardID)

            commit(types.SET_ACTIVE_DASHBOARD, dashboard)

            if (!getters.getActiveWidgetGroupID && dashboard.WidgetGroupList.length > 0) {
                commit(types.SET_ACTIVE_WIDGET_GROUP_ID, dashboard.WidgetGroupList[0].WidgetGroupID)
            }

            // Remove list of widgets from previous dashboard
            commit(
                types.UPDATE_DASHBOARD_DATA,
                previousDashboard,
                {
                    WidgetGroupList: []
                }
            )
        }

        commit(types.SET_LOADING, true)

        if (loadCached && state.activeDashboard) {
            load()
        } else {
            await load()
        }

        commit(types.SET_LOADING, false)
    },
    async createDashboard({ commit }, dashboard) {
        dashboard = await DashboardApi.store(dashboard)
        commit(types.ADD_DASHBOARD, dashboard)
        commit(types.SET_ACTIVE_DASHBOARD, dashboard)
    },
    async updateDashboard({ commit }, dashboard) {
        commit(types.UPDATE_DASHBOARD, dashboard)
        commit(types.SET_LOADING, false)
    },
    setLoadingData({ commit }, value) {
        commit(types.SET_LOADING, value)
    },
    setContentLoading({ commit }, value) {
        commit(types.SET_CONTENT_LOADING, value)
    },
    async deleteDashboard({ commit }, dashboard) {
        await DashboardApi.destroy(dashboard.DashboardID)
        commit(types.DELETE_DASHBOARD, dashboard)
    },
    async setActiveGroup({ commit }, group) {
        commit(types.SET_ACTIVE_WIDGET_GROUP, group)
    },
    async updateDashboardLayout({ commit, state }, LayoutID) {
        let dashboard = state.activeDashboard
        dashboard['DashboardLayoutID'] = LayoutID
        await DashboardApi.update(dashboard)
        commit(types.SET_ACTIVE_DASHBOARD, dashboard)
    },
    async updateCurrentDashboard({ commit }, dashboard) {
        commit(types.UPDATE_CURRENT_DASHBOARD, dashboard)
    },
    async updateGroupList({ commit }, value) {
        commit(types.UPDATE_GROUP_LIST, value)
    },
}

const getters = {
    getActiveDashboard: state => {
        return state.activeDashboard
    },
    getEditModeState: state => {
        return state.editMode
    },
    getActiveWidgetGroup: state => {
        return state.activeWidgetGroup
    },
    getAllDashboards: state => {
        return state.allDashboards
    },
    getActiveWidgetGroupID: state => {
        return state.activeWidgetGroupID
    },
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}
